/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import Imgix from 'js/components/Imgix';
import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Link, Typography2 } from '@coursera/cds-core';
import type { SectionName } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import { noheadshotUrl } from 'bundles/partners-graphql/constants';
import DottedSeparator from 'bundles/unified-description-page-common/components/shared/DottedSeparator';
import TopInstructorBadge from 'bundles/unified-description-page-common/components/shared/instructors/TopInstructorBadge';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';
import type { InstructorFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/SharedCommonEntitiesFragments';
import { getInstructorPageUrl } from 'bundles/unified-description-page-common/utils';

import _t from 'i18n!nls/xdp';

const styles = {
  container: css`
    display: flex;
  `,
  centeredContainer: css`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  `,
  instructorPortrait: () => css`
    border-radius: 50%;
    background-color: var(--cds-color-white-0);
    padding: 2px;
    height: 50px;
    width: 50px;
    border: 1px solid var(--cds-color-grey-50);
    overflow: hidden;
    flex-shrink: 0;
    margin-right: var(--cds-spacing-150);
  `,
  instructorPortraitImage: css`
    height: 100%;
    border-radius: 50%;
  `,
  instructorLink: () => css`
    text-decoration-color: var(--cds-color-grey-975);
  `,
  topInstructorBadgeContainer: () => css`
    margin-bottom: var(--cds-spacing-50);
  `,
};

type Props = {
  instructor: InstructorFragment;
  relatedPartnerName?: string | null;
  disableCTA?: boolean;
  trackingLocation?: SectionName;
};

const InstructorItem = ({ instructor, relatedPartnerName, disableCTA, trackingLocation }: Props) => {
  const styledInstructorName = (
    <Typography2 component="span" variant="subtitleMedium">
      {instructor.fullName}
    </Typography2>
  );

  const track = useTracker();
  const eventingProductData = useEventingProductData();
  const onClickInstructorLink = () => {
    track('click_button', {
      button: { name: 'description_page_instructor' },
      ...(trackingLocation
        ? {
            pageSection: {
              sectionName: trackingLocation,
            },
          }
        : {}),
      ...eventingProductData,
    });
  };

  return (
    <div css={styles.container}>
      <div css={styles.instructorPortrait}>
        <Imgix
          src={instructor.photo || noheadshotUrl}
          alt={instructor.fullName}
          maxHeight={75}
          maxWidth={75}
          css={styles.instructorPortraitImage}
          imgParams={{ fit: 'crop' }}
        />
      </div>
      <div>
        {instructor.isTopInstructor && (
          <div css={styles.topInstructorBadgeContainer}>
            <TopInstructorBadge />
          </div>
        )}
        <div>
          {disableCTA ? (
            styledInstructorName
          ) : (
            <Link
              component={TrackedLink2}
              href={getInstructorPageUrl(instructor.id, instructor.shortName)}
              trackingName="hero_instructor"
              typographyVariant="bodyPrimary"
              css={styles.instructorLink}
              onClick={onClickInstructorLink}
            >
              {styledInstructorName}
            </Link>
          )}
        </div>
        {relatedPartnerName && (
          <Typography2 variant="bodySecondary" component="div">
            {relatedPartnerName}
          </Typography2>
        )}
        <div css={styles.centeredContainer}>
          <Typography2 variant="bodySecondary" component="span">
            <FormattedMessage
              message={_t('{coursesCount, number} {coursesCount, plural, =1 {Course} other {Courses}}')}
              coursesCount={instructor.coursesTaught}
            />
          </Typography2>
          <DottedSeparator />
          <Typography2 variant="bodySecondary" component="span">
            <FormattedMessage
              message={_t('{learnersCount, number} {learnersCount, plural, =1 {learner} other {learners}}')}
              learnersCount={instructor.learnersReached}
            />
          </Typography2>
        </div>
      </div>
    </div>
  );
};

export default InstructorItem;
